import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Presentation, FileText, LayoutGrid, Loader2, Search, BookOpen, Pencil, ChevronDown, FileDown } from 'lucide-react';
import { getDetailedTopicBlocks, searchDetailedTopics, DetailedTopic } from '@/types/oposiciones';
import { getDetailedTopicBlocksC1, searchDetailedTopicsC1 } from '@/types/oposicionesC1';
import { getCuerpoTopicBlocks, searchCuerpoTopics, CuerpoVariant } from '@/data/temariosNuevosCuerpos';
import { getFormatLabel, GammaFormat } from '@/services/gammaService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { usePendingGenerations } from '@/context/pending-generations.context';
import { saveDownload, DownloadType } from '@/services/downloadHistoryService';
import { checkOpositorQuota } from '@/services/opositorQuotaService';
import { QuotaUpgradeModal } from './QuotaUpgradeModal';
import { Badge } from '@/components/ui/badge';
import { TEMARIO_IIPP, TEMARIO_GUARDIA_CIVIL, TEMARIO_C2_AUXILIAR, TEMARIO_POLICIA_NACIONAL, TEMARIO_C1_ADMINISTRATIVO } from '@/data/temariosNuevosCuerpos';
import type { CuerpoTopic } from '@/data/temariosNuevosCuerpos';
import { TEMARIO_GENERICO } from '@/data/temarioGenerico';
import type { TopicDevelopmentBlueprint } from '@/data/topicDevelopmentBlueprints';

interface TopicGeneratorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: 'secretaria' | 'c1' | 'guardia_civil' | 'iipp' | 'c2' | 'policia_nacional';
}

type ExportType = 'word' | 'pdf';

const FORMAT_OPTIONS = [
  { value: 'presentation' as GammaFormat, icon: Presentation, label: 'Presentación', desc: 'Slides para estudiar' },
  { value: 'resumen' as GammaFormat, icon: FileText, label: 'Resumen A4', desc: 'Documento para comprender' },
  { value: 'infografia' as GammaFormat, icon: LayoutGrid, label: 'Infografía A4', desc: 'Esquema para memorizar' },
];

export function TopicGeneratorModal({ open, onOpenChange, variant = 'secretaria' }: TopicGeneratorModalProps) {
  const isC1 = variant === 'c1';
  const isCuerpoNuevo = variant === 'iipp' || variant === 'guardia_civil' || variant === 'c2' || variant === 'policia_nacional';
  const cuerpoVariant = isCuerpoNuevo ? (variant as CuerpoVariant) : null;

  const downloadMode = (
    variant === 'c1' ? 'oposiciones_c1'
    : variant === 'iipp' ? 'oposiciones_iipp'
    : variant === 'guardia_civil' ? 'oposiciones_guardia_civil'
    : variant === 'policia_nacional' ? 'oposiciones_policia_nacional'
    : variant === 'c2' ? 'oposiciones_c2'
    : 'oposiciones'
  ) as any;

  const examTypeForResumen =
    variant === 'c1' ? 'c1'
    : variant === 'iipp' ? 'iipp'
    : variant === 'guardia_civil' ? 'guardia_civil'
    : variant === 'policia_nacional' ? 'policia_nacional'
    : variant === 'c2' ? 'c2'
    : 'sial';

  const { user, profile } = useAuth();
  const oposLabel =
    variant === 'c1' ? 'Cuerpo General Administrativo del Estado (C1)'
    : variant === 'iipp' ? 'Ayudante de Instituciones Penitenciarias'
    : variant === 'guardia_civil' ? 'Guardia Civil'
    : variant === 'policia_nacional' ? 'Policía Nacional'
    : variant === 'c2' ? 'Auxiliar Administrativo del Estado (C2)'
    : 'Secretaría-Intervención';

  const [tab, setTab] = useState<'topic' | 'concept'>('topic');
  const [selectedTopic, setSelectedTopic] = useState<DetailedTopic | null>(null);
  const [conceptQuery, setConceptQuery] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<GammaFormat>('presentation');
  const [exportType, setExportType] = useState<ExportType>('word');
  const [topicFilter, setTopicFilter] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [openBlocks, setOpenBlocks] = useState<Set<string>>(new Set());
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);
  const [quotaLimitForModal, setQuotaLimitForModal] = useState(2);
  const { toast } = useToast();
  const { runBackgroundGamma, runBackgroundResumen } = usePendingGenerations();

  const allBlocks = useMemo(() => {
    if (cuerpoVariant) {
      return { all: getCuerpoTopicBlocks(cuerpoVariant) as any };
    }
    if (isC1) {
      return { all: getDetailedTopicBlocksC1() };
    }
    return {
      comun: getDetailedTopicBlocks('comun'),
      especifico: getDetailedTopicBlocks('especifico'),
    };
  }, [isC1, cuerpoVariant]);

  const filteredTopics = useMemo(() => {
    if (!topicFilter.trim()) return null;
    if (cuerpoVariant) return searchCuerpoTopics(cuerpoVariant, topicFilter) as any;
    return isC1 ? searchDetailedTopicsC1(topicFilter) : searchDetailedTopics(topicFilter);
  }, [topicFilter, isC1, cuerpoVariant]);

  const toggleBlock = (blockId: string) => {
    setOpenBlocks(prev => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  const isResumenLocal = selectedFormat === 'resumen';

  const getBlueprintExamType = () => {
    if (variant === 'secretaria') return 'base_comun';
    if (variant === 'c1') return 'c1_administrativo';
    return examTypeForResumen;
  };

  const formatList = (items: string[], emptyText: string) => (
    items.length ? items.map((item) => `- ${item}`) : [`- ${emptyText}`]
  );

  const formatArticulosClave = (blueprint?: TopicDevelopmentBlueprint) => {
    const articulos = blueprint?.articulosClave ?? [];
    if (!articulos.length) {
      return ['- Pendiente: identificar artículos clave o fuente técnica equivalente antes de cerrar el material.'];
    }

    return articulos.map((item) => {
      const refs = item.articulos.length ? item.articulos.join(', ') : 'pendiente de identificar';
      return `- ${item.norma}: ${refs}. ${item.criterio} [${item.estado}]`;
    });
  };

  const formatSubepigrafes = (blueprint: TopicDevelopmentBlueprint) => {
    if (!blueprint.subepigrafes.length) {
      return blueprint.index.map((item, idx) => `${idx + 1}. ${item}`);
    }

    return blueprint.subepigrafes.flatMap((epigrafe, idx) => {
      const sectionNumber = epigrafe.id || `${idx + 1}`;
      return [
        `${sectionNumber}. ${epigrafe.title}`,
        ...epigrafe.children.map((child, childIdx) => `   ${sectionNumber}.${childIdx + 1}. ${child}`),
      ];
    });
  };

  const buildGenerationBrief = async () => {
    const lookupTopic = (): CuerpoTopic | undefined => {
      if (tab !== 'topic' || !selectedTopic) return undefined;
      const num = selectedTopic.number;
      if (variant === 'iipp') return TEMARIO_IIPP.find((t) => t.number === num);
      if (variant === 'guardia_civil') return TEMARIO_GUARDIA_CIVIL.find((t) => t.number === num);
      if (variant === 'c2') return TEMARIO_C2_AUXILIAR.find((t) => t.number === num);
      if (variant === 'policia_nacional') return TEMARIO_POLICIA_NACIONAL.find((t) => t.number === num);
      if (variant === 'c1') return TEMARIO_C1_ADMINISTRATIVO.find((t) => t.number === num);
      if (variant === 'secretaria') return TEMARIO_GENERICO.find((t) => t.number === num);
      return undefined;
    };

    const topicData = lookupTopic();
    const blueprint = topicData
      ? (await import('@/data/topicDevelopmentBlueprints')).getTopicDevelopmentBlueprint(getBlueprintExamType(), topicData.id)
      : undefined;

    if (tab === 'topic' && selectedTopic) {
      const indexItems = blueprint?.subepigrafes?.length || blueprint?.index?.length
        ? formatSubepigrafes(blueprint)
        : topicData?.index?.length
          ? topicData.index.map((item, idx) => `${idx + 1}. ${item}`)
          : [
              '1. Introducción y encaje normativo',
              '2. Desarrollo sistemático del tema',
              '3. Conceptos obligatorios y relaciones entre normas',
              '4. Tablas, esquemas y claves de comprensión',
              '5. Reformas relevantes',
            ];
      const normativa = blueprint?.normativa?.length
        ? blueprint.normativa
        : topicData?.normativa?.length
          ? topicData.normativa
          : [];
      const conceptos = blueprint?.conceptosObligatorios ?? [];
      const articulosClave = formatArticulosClave(blueprint);
      const tablas = blueprint?.tablasRecomendadas ?? [];
      const graficos = blueprint?.elementosGraficos ?? [];
      const reformas = blueprint?.reformasRelevantes ?? [];
      const reutilizacion = blueprint?.reuseFromBaseComun?.length
        ? blueprint.reuseFromBaseComun.join(', ')
        : 'Sin reutilización directa detectada; desarrollar el tema desde su normativa propia.';

      return [
        `MUNILEX Academy`,
        `Tema ${selectedTopic.number}. ${selectedTopic.name}`,
        `Oposición: ${oposLabel}`,
        '',
        'OBJETIVO DEL MATERIAL',
        'Redactar un PDF de contenido del tema, no una ficha breve. Debe servir para estudiar, comprender y repasar con seguridad jurídica.',
        'Extensión orientativa: desarrollo amplio, con explicación suficiente de cada epígrafe, ejemplos jurídicos cuando ayuden y cierre de conceptos confundibles.',
        '',
        'MATRIZ ATL DEL TEMA: INFORMACIÓN QUE LA IA DEBE TENER EN CUENTA',
        'Esta matriz es contexto interno de generación. No debe aparecer como bloque literal en el material final, pero sí debe reflejarse en el contenido.',
        '',
        'LEYES, NORMAS O FUENTES BASE',
        ...formatList(normativa, 'Determinar normativa aplicable con fuente oficial antes de cerrar el material.'),
        '',
        'ARTÍCULOS CLAVE O UNIDADES EQUIVALENTES',
        ...articulosClave,
        '',
        'CONCEPTOS OBLIGATORIOS QUE DEBEN DESARROLLARSE',
        ...formatList(conceptos, 'Identificar conceptos troncales del tema y explicarlos de forma expresa.'),
        '',
        'REFORMAS RELEVANTES POR NORMA',
        ...(reformas.length
          ? reformas.map((item) => `- ${item.norma}: ${item.criterio} [${item.estado}]`)
          : ['- Identificar la reforma, hito reformador o actualización normativa más importante de cada norma aplicada.']),
        '',
        'ÍNDICE DEL TEMA',
        ...indexItems,
        '',
        'TABLAS RECOMENDADAS',
        ...formatList(tablas, 'Incluir al menos una tabla comparativa o de síntesis si mejora la comprensión.'),
        '',
        'ELEMENTOS GRÁFICOS RECOMENDADOS',
        ...formatList(graficos, 'Incluir esquema, flujo, matriz o mapa visual si ayuda a memorizar.'),
        '',
        'REUTILIZACIÓN DE BASE COMÚN',
        `- ${reutilizacion}`,
        '',
        'REGLAS DE REDACCIÓN',
        '- Encabezado sobrio: MUNILEX Academy, número de tema, título e Índice del tema.',
        '- No incluir secciones preliminares comerciales, apartados de planificación del examen ni bloques internos de verificación como secciones visibles.',
        '- Usar negrita en palabras importantes, instituciones, principios, plazos, órganos, procedimientos y diferencias críticas.',
        '- Desarrollar todos los subepígrafes: no limitarse a mencionarlos.',
        '- Cuando una ley tenga varias reformas relevantes, explicarlas por separado y decir por qué importan para oposición.',
        '- Incluir tablas o elementos visuales cuando ayuden a distinguir conceptos, órganos, fases, plazos, derechos o recursos.',
        '- Mantener tono académico claro, útil para opositores y jurídicamente prudente.',
      ].join('\n');
    }

    return [
      `MUNILEX Academy`,
      `Concepto: ${conceptQuery.trim()}`,
      `Oposición: ${oposLabel}`,
      '',
      'OBJETIVO DEL MATERIAL',
      'Redactar un desarrollo amplio, con explicación jurídica clara, normativa aplicable, tablas o esquemas si ayudan y claves de comprensión para opositores.',
      '',
      'ÍNDICE DEL TEMA',
      `1. Marco general`,
      `2. Normativa aplicable`,
      `3. Conceptos esenciales`,
      `4. Desarrollo sistemático`,
      `5. Tablas, esquemas y relaciones prácticas`,
      `6. Reformas relevantes`,
      '',
      'REGLAS DE REDACCIÓN',
      '- No incluir secciones preliminares comerciales, apartados de planificación del examen ni bloques internos de verificación como secciones visibles.',
      '- Usar negrita en palabras importantes.',
      '- Explicar diferencias, límites, plazos, órganos y errores frecuentes cuando proceda.',
      '- Contrastar siempre con normativa oficial vigente antes de cerrar el contenido.',
    ].join('\n');
  };

  const handleGenerate = async () => {
    if (isGenerating) return;

    const hasInput = tab === 'topic' ? selectedTopic : conceptQuery.trim();
    if (!hasInput) {
      toast({ title: 'Selecciona un tema o escribe un concepto', variant: 'destructive' });
      return;
    }

    // Plan free (opositor): limitar materiales del mes
    if (user) {
      const quota = await checkOpositorQuota(user.id, (profile as any)?.plan, profile, 'materials');
      if (!quota.allowed) {
        setQuotaLimitForModal(quota.limit);
        setQuotaModalOpen(true);
        return;
      }
    }

    setIsGenerating(true);
    setStatusMessage('Iniciando generación...');
    const label = getFormatLabel(selectedFormat);
    const topicTitle = selectedTopic ? `Tema ${selectedTopic.number}: ${selectedTopic.name}` : conceptQuery.trim();
    let generationBrief = '';
    try {
      generationBrief = await buildGenerationBrief();
    } catch (error: any) {
      toast({
        title: 'No se pudo preparar el tema',
        description: error?.message || 'Error cargando el inventario maestro del temario.',
        variant: 'destructive',
      });
      setIsGenerating(false);
      setStatusMessage(null);
      return;
    }

    if (isResumenLocal) {
      runBackgroundResumen({
        topic: topicTitle,
        topicId: selectedTopic?.id,
        cuerpo: variant,
        userId: user?.id,
        mode: downloadMode,
        exportType,
        content: generationBrief
      });
      
      toast({
        title: "Generación iniciada",
        description: "Estamos redactando el resumen en segundo plano. Te avisaremos al terminar.",
      });
      onOpenChange(false);
    } else {
      runBackgroundGamma({
        topic: topicTitle,
        topicId: selectedTopic?.id,
        format: selectedFormat,
        cuerpo: variant,
        userId: user?.id,
        mode: downloadMode,
        content: generationBrief
      });

      toast({
        title: "Generación en Gamma iniciada",
        description: `Estamos creando tu ${label.toLowerCase()}. Puedes seguir navegando.`,
      });
      onOpenChange(false);
    }
    
    setIsGenerating(false);
    setStatusMessage(null);
  };

  const renderTopicButton = (topic: DetailedTopic) => (
    <button
      key={topic.id}
      onClick={() => setSelectedTopic(topic)}
      className={`w-full text-left px-3 py-1.5 rounded-md text-[13px] transition-colors ${
        selectedTopic?.id === topic.id
          ? 'bg-violet-100 text-violet-900 border border-violet-300 dark:bg-violet-900/30 dark:text-violet-200 dark:border-violet-700'
          : 'hover:bg-muted/50 text-foreground'
      }`}
    >
      <span className="font-medium leading-snug">
        <span className="text-muted-foreground mr-1.5">T{topic.number}</span>
        {topic.name}
      </span>
    </button>
  );

  const renderBlockGroup = (blocks: { blockId: string; block: string; topics: DetailedTopic[] }[], categoryLabel: string) => (
    <div className="mb-2">
      <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 px-1">
        {categoryLabel}
      </h4>
      <div className="space-y-0">
        {blocks.map(({ blockId, block, topics }) => (
          <Collapsible key={blockId} open={openBlocks.has(blockId)} onOpenChange={() => toggleBlock(blockId)}>
            <CollapsibleTrigger className="w-full flex items-center justify-between px-3 py-1 rounded-lg text-[13px] font-medium hover:bg-muted/50 transition-colors text-foreground">
              <span>{block} <span className="text-muted-foreground font-normal">({topics.length})</span></span>
              <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${openBlocks.has(blockId) ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pl-2 space-y-0 mt-0 mb-1">
                {topics.map(renderTopicButton)}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </div>
  );

  const renderSearchResults = (topics: DetailedTopic[]) => {
    if (topics.length === 0) {
      return <p className="text-sm text-muted-foreground text-center py-4">No se encontraron temas para "{topicFilter}"</p>;
    }
    return <div className="space-y-0.5">{topics.map(renderTopicButton)}</div>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[88vw] max-w-[52rem] max-h-[600px] flex flex-col overflow-y-auto p-2.5 sm:p-3.5 gap-2 text-sm">
        <DialogHeader className="space-y-0.5">
          <DialogTitle className="flex items-center gap-2 text-base">
            <BookOpen className="h-4 w-4 text-violet-600" />
            Generar material de estudio
          </DialogTitle>
          <DialogDescription className="text-xs">
            Genera presentaciones, resúmenes o infografías desde el temario oficial de {oposLabel} o un concepto libre.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'topic' | 'concept')} className="flex flex-col">
          <TabsList className="grid h-9 w-full grid-cols-2">
            <TabsTrigger value="topic" className="flex items-center gap-1.5 text-xs">
              <BookOpen className="h-3.5 w-3.5" />
              Tema del temario
            </TabsTrigger>
            <TabsTrigger value="concept" className="flex items-center gap-1.5 text-xs">
              <Pencil className="h-3.5 w-3.5" />
              Concepto libre
            </TabsTrigger>
          </TabsList>

          <TabsContent value="topic" className="flex flex-col mt-1">
            <div className="relative mb-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar tema por nombre o número..."
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
            {filteredTopics ? (
              <ScrollArea className="h-[180px] border rounded-lg p-1.5 bg-background">
                {renderSearchResults(filteredTopics)}
              </ScrollArea>
            ) : cuerpoVariant ? (
              <ScrollArea className="h-[180px] border rounded-lg p-1.5 bg-background">
                {renderBlockGroup((allBlocks as any).all, `Temario oficial — ${oposLabel}`)}
              </ScrollArea>
            ) : isC1 ? (
              <ScrollArea className="h-[180px] border rounded-lg p-1.5 bg-background">
                {renderBlockGroup((allBlocks as any).all, 'Temario C1 — 6 Bloques')}
              </ScrollArea>
            ) : (
              <div className="grid grid-cols-2 gap-1.5">
                <ScrollArea className="h-[180px] border rounded-lg p-1.5 bg-background">
                  {renderBlockGroup((allBlocks as any).comun, 'Bloque Común')}
                </ScrollArea>
                <ScrollArea className="h-[180px] border rounded-lg p-1.5 bg-background">
                  {renderBlockGroup((allBlocks as any).especifico, 'Bloque Específico')}
                </ScrollArea>
              </div>
            )}
            {selectedTopic && (
              <div className="mt-1 px-2.5 py-1 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 text-xs shrink-0">
                <span className="text-violet-700 dark:text-violet-300">
                  Seleccionado: <strong>Tema {selectedTopic.number}</strong> — {selectedTopic.name}
                </span>
              </div>
            )}
          </TabsContent>

          <TabsContent value="concept" className="mt-1">
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                Escribe un concepto {isC1 ? 'del temario C1' : 'jurídico'} concreto (ej: {isC1 ? '"nóminas de personal", "procedimiento administrativo"' : '"silencio administrativo", "contrato menor"'}).
              </p>
              <Input
                placeholder={isC1 ? 'Ej: nóminas de personal' : 'Ej: silencio administrativo'}
                value={conceptQuery}
                onChange={(e) => setConceptQuery(e.target.value)}
                className="text-sm h-9"
              />
            </div>
          </TabsContent>
        </Tabs>


        <div className="space-y-1 shrink-0">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Formato</p>
          <div className="grid grid-cols-3 gap-1.5">
            {FORMAT_OPTIONS.map(({ value, icon: Icon, label, desc }) => (
              <button
                key={value}
                onClick={() => setSelectedFormat(value)}
                className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border text-center transition-all ${
                  selectedFormat === value
                    ? 'border-violet-400 bg-violet-50 text-violet-700 dark:border-violet-600 dark:bg-violet-900/30 dark:text-violet-300'
                    : 'border-border hover:border-violet-300 hover:bg-violet-50/50 dark:hover:border-violet-700 dark:hover:bg-violet-900/20'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">{label}</span>
                <span className="text-[11px] text-muted-foreground leading-tight">{desc}</span>
              </button>
            ))}
          </div>
        </div>

        {isResumenLocal && (
          <div className="space-y-1 shrink-0">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Formato de descarga</p>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={() => setExportType('word')}
                className={`flex items-center justify-center gap-2 py-1.5 px-2 rounded-lg border text-xs font-medium transition-all ${
                  exportType === 'word'
                    ? 'border-violet-400 bg-violet-50 text-violet-700 dark:border-violet-600 dark:bg-violet-900/30 dark:text-violet-300'
                    : 'border-border hover:border-violet-300 hover:bg-violet-50/50'
                }`}
              >
                <FileDown className="h-3.5 w-3.5" />
                Word (.docx)
              </button>
              <button
                onClick={() => setExportType('pdf')}
                className={`flex items-center justify-center gap-2 py-1.5 px-2 rounded-lg border text-xs font-medium transition-all ${
                  exportType === 'pdf'
                    ? 'border-violet-400 bg-violet-50 text-violet-700 dark:border-violet-600 dark:bg-violet-900/30 dark:text-violet-300'
                    : 'border-border hover:border-violet-300 hover:bg-violet-50/50'
                }`}
              >
                <FileText className="h-3.5 w-3.5" />
                PDF
              </button>
            </div>
          </div>
        )}

        {isGenerating && statusMessage && (
          <div className="text-xs text-center text-violet-600 dark:text-violet-400 animate-pulse flex items-center justify-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {statusMessage}
          </div>
        )}

        <div className="sticky bottom-0 left-0 right-0 bg-background pt-1.5 border-t -mx-2.5 sm:-mx-3.5 px-2.5 sm:px-3.5 pb-0.5">
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || (tab === 'topic' ? !selectedTopic : !conceptQuery.trim())}
            className="w-full h-9 text-sm bg-violet-600 hover:bg-violet-700 text-white"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Generando...
              </>
            ) : (
              <>
                {isResumenLocal ? <FileDown className="h-4 w-4 mr-2" /> : <Presentation className="h-4 w-4 mr-2" />}
                {isResumenLocal
                  ? `Generar Resumen (${exportType === 'word' ? 'Word' : 'PDF'})`
                  : `Generar ${getFormatLabel(selectedFormat)}`
                }
              </>
            )}
          </Button>
        </div>
      </DialogContent>
      <QuotaUpgradeModal
        open={quotaModalOpen}
        onOpenChange={setQuotaModalOpen}
        title="Materiales mensuales agotados"
        description={`Has agotado tus ${quotaLimitForModal} materiales del plan gratuito este mes.`}
        options={['pro']}
      />
    </Dialog>
  );
}
